import { uniqBy } from 'lodash-es';
import { mapActions, mapState, mapGetters } from 'vuex';
import InsightsTracking from './InsightsTrackingMixin';
import { INSIGHTS_TIME_ZONE_MODE_MEMBERS, INSIGHTS_MEMBERS_TIME_ZONE_OPTION } from '~/app_constants';
import { makeUserTimeZone } from '~/utils/time_utils';
import { isMembersTimeZoneEligible, isMemberRowEligible, multiDayZoneReset } from '~/utils/insights_filter';

// Constants for tab names to avoid magic strings
const TABS = {
  TIMELINE: 'timeline',
  UNUSUAL_ACTIVITIES: 'unusual-activity',
};

const isDayPeriod = (date) => {
  const from = date?.from ? moment(date.from) : null;
  const to = date?.to ? moment(date.to) : null;

  return from && to ? from.isSame(to, 'day') : false;
};

export default {
  mixins: [InsightsTracking],
  data: () => ({
    filterSettings: Object.freeze({
      allowClear: false,
      multiple: false,
      placeholder: 'Loading...',
      minimumResultsForSearch: 8,
    }),
    dateFilterTimeout: null,
  }),
  watch: {
    users(val) {
      if (val.length) {
        this.populateTimeZoneOptions();
      }
    },
  },
  computed: {
    ...mapState(['organization', 'filters', 'previousView', 'current_user_time_zone', 'user_id', 'can_see_team']),
    ...mapGetters([
      'selectedFilter',
      'isEverythingReady',
      'users',
      'teams',
      'timeZoneOptions',
      'activeTimeZone',
      'dashboardType',
      'isOwner',
      'isTeamLead',
      'isManager',
      'previousDashboardIsIndividualTeam',
      'currentUserBelongsToPrevTeam',
      'previousDashboardTeamName',
    ]),
    backButtonText() {
      let text = 'All members';

      if (this.previousDashboardIsIndividualTeam && this.currentUserBelongsToPrevTeam) {
        text = this.previousDashboardTeamName;
      }

      return text;
    },
    /**
     * Determines if widget reloading should be skipped for filter changes.
     * Currently, the timeline tab manages its own widget loading state,
     * so we skip global widget reload to prevent UI conflicts.
     */
    shouldSkipWidgetReload() {
      return this.filters.tab === TABS.TIMELINE;
    },
    // Unusual Activity's own widget container already refetches itself when the zone changes
    // (its own time_zone/time_zone_mode watchers), preserving local sort/page state. Letting the
    // generic tab-wide reload also fire on a zone change races that refetch and can overwrite it
    // with a plain, unsorted response.
    ownsTimeZoneRefetch() {
      return this.filters.tab === TABS.TIMELINE || this.filters.tab === TABS.UNUSUAL_ACTIVITIES;
    },
  },
  methods: {
    ...mapActions([
      'setTimeZoneOptions',
      'setFilter',
      'setShowBenchmarksAverages',
      'fetchEngagementScores',
      'setPreviousView',
      'setSelectedFilter',
    ]),
    setDateFilter({ query }) {
      // Clear any existing timeout
      if (this.dateFilterTimeout) {
        clearTimeout(this.dateFilterTimeout);
      }

      // Debounce the global filter updates and API calls.
      this.dateFilterTimeout = setTimeout(() => {
        // Skip widget reload on timeline tab since it manages its own loading state, and on
        // initial mount to prevent double-fetching with TheInsights created hook. The selected
        // timezone is preserved during initial hydration (resetTimeZone: false).
        this.applyScopeChange({ date: query.value }, {
          resetTimeZone: !query.initial,
          skipWidgets: query.initial,
        });

        // Only fetch engagement scores when navigation sidebar is visible
        // (engagement scores are only displayed in the navigation sidebar, which is hidden on timeline tab).
        // On initial mount the navigation list fetches the scores for its own members.
        if (this.filters.tab !== TABS.TIMELINE && !query.initial) {
          this.fetchEngagementScores();
        }
      }, 300);

      // Analytics tracking (keep immediate for accurate user interaction tracking)
      // Skip tracking when:
      // 1. query.initial is true (initial load)
      // 2. query.skipTracking is true (implicit date change from member/team selection)
      if (!query.initial && !query.skipTracking) {
        const to = new Date(query.value.to).getTime();
        const from = new Date(query.value.from).getTime();

        this.trackAnalytics('Insights date apply', {
          days_in_range: (to - from) / (1000 * 3600 * 24),
          preset_value: query.value.id,
          type: 'Date picker',
          statsigCustomIDs: ['organizationID', this.organization.id],
        });
      }
    },

    setMembersWithPreviousView(string, teamId = null, timeZone = null, extraChanges = {}) {
      // A cleared selection (null/undefined) means All Members.
      const scope = string ?? 'all_user_id';

      this.setSelectedFilter(scope);
      this.setPreviousView({ previousView: teamId || this.selectedFilter, currentView: scope });
      // Give time to the team filter to settle setting the members filter
      this.$nextTick(() => {
        this.setMembersFilter(scope, timeZone, extraChanges);
      });
    },

    setMembersFilter(member, timeZone = null, extraChanges = {}) {
      const memberScope = member ?? 'all_user_id';
      let initialValue;
      let initialFilterType;

      if (this.filters.user_id) {
        initialValue = String(this.filters.user_id);
        initialFilterType = 'user';
      } else if (this.filters.team_id) {
        initialValue = String(this.filters.team_id);
        initialFilterType = 'team';
      } else {
        initialValue = initialFilterType = 'all';
      }

      const [id, prefix] = [...memberScope.split(/_(.*)/s)];
      const opposite = prefix === 'user_id' ? 'team_id' : 'user_id';

      this.applyScopeChange({ [prefix]: id, [opposite]: null, ...extraChanges }, { memberTimeZone: timeZone });

      let event;

      switch (this.filters.tab) {
        case TABS.TIMELINE:
          event = 'Timeline filter changed';
          break;
        default:
          event = 'Insights filters changed';
      }

      this.trackAnalytics(event, {
        initial_value: initialValue,
        initial_filter_type: initialFilterType,
        new_value: id,
        filter_type: id === 'all' ? 'all' : prefix.split('_')[0],
      });
    },

    // Reads the applied filters by default; callers pass overrides to evaluate a pending change
    // (new date/member) before it is written, so the timezone options can be batched in one apply.
    timeZoneOptionsForCurrentFilter({ date = this.filters?.date, userId = this.filters?.user_id, timeZone = null } = {}) {
      const dayPeriod = isDayPeriod(date);
      // Deduped by `mode`: a member in the org's zone is still its own labelled row.
      const listed = (options) => uniqBy(options.filter(Boolean), (tz) => tz.mode ?? tz.for);

      // Org and My zones always; plus Members' zones for many members, or the member's own for one.
      const options = [this.organization.time_zone, this.current_user_time_zone];
      const singleMember = userId && userId !== 'all';

      if (!singleMember) {
        // Members' zones names other members' rows, so it only makes sense for a viewer who can see the team.
        const perRow = this.can_see_team && isMembersTimeZoneEligible(this.filters?.tab, dayPeriod);

        return listed(perRow ? [...options, INSIGHTS_MEMBERS_TIME_ZONE_OPTION] : options);
      }

      // Over a range the member's own row exists only where a picked zone stays.
      if (!isMemberRowEligible(this.filters?.tab, dayPeriod)) {
        return listed(options);
      }

      // Most current first: a clicked Timeline row, the sidebar list, then the server-rendered
      // option (Timeline never loads `this.users`) - only while it still belongs to the applied member -
      // and the viewer's own zone when the member is the viewer (restored from the session, no reload).
      const memberZone = timeZone
        || this.users?.find((u) => u.id === +userId)?.time_zone
        || (+userId === +this.filters?.user_id && this.timeZoneOptions?.find((tz) => tz.mode === 'user'))
        || (+userId === +this.user_id && this.current_user_time_zone?.for);

      if (!memberZone) {
        return listed(options);
      }

      const member = typeof memberZone === 'string'
        ? makeUserTimeZone("User's time zone", memberZone)
        : memberZone;

      return listed([this.organization.time_zone, member, this.current_user_time_zone]);
    },
    // Sets the timezone dropdown options when a set was resolved (no-op when none), so the callers
    // below do not each re-implement the presence check.
    applyTimeZoneOptions(options) {
      if (options) {
        this.setTimeZoneOptions(options);
      }
    },
    // tz_options drives the dropdown only - never use it to validate filters.time_zone.
    populateTimeZoneOptions() {
      this.applyTimeZoneOptions(this.timeZoneOptionsForCurrentFilter());
    },
    // Returns the filter changes that put a selection back to a valid one when the given filters'
    // timezone (or Members' mode) is not among the given options; empty when it is still valid.
    invalidTimeZoneReset(options, { time_zone: current, time_zone_mode: mode } = this.filters || {}) {
      const resets = {};

      if (!options) {return resets;}

      if (current && !options.some((tz) => tz.for === current)) {
        resets.time_zone = this.organization.time_zone.for;
        resets.time_zone_mode = null;
      }
      // The mode names the picked option ('members', 'mine', ...); it goes when that row does.
      if (mode && !options.some((tz) => tz.mode === mode)) {
        resets.time_zone_mode = null;
      }

      return resets;
    },

    // A zone restored from the URL or sessionStorage can be stale (e.g. the member's or viewer's zone
    // changed), and nothing re-validates it until a filter changes. Run once on load. A single member is
    // validated only once their zone is listed (server-rendered or loaded) where their row is on offer,
    // and a stale "User's time zone" pick follows the member to their current zone.
    // All-members Timeline lands on Members' zones, since that is its default view.
    resetStaleTimeZone() {
      const { user_id: userId, time_zone_mode: mode, tab, date } = this.filters || {};
      const singleMember = userId && userId !== 'all';
      const options = this.timeZoneOptionsForCurrentFilter();
      const memberOption = options.find((tz) => tz.mode === 'user');

      if (singleMember && !memberOption && isMemberRowEligible(tab, isDayPeriod(date))) {return;}

      const resets = this.invalidTimeZoneReset(options);

      if (!Object.keys(resets).length) {return;}

      if (options.some((tz) => tz.mode === INSIGHTS_TIME_ZONE_MODE_MEMBERS)) {
        resets.time_zone_mode = INSIGHTS_TIME_ZONE_MODE_MEMBERS;
      } else if (mode === 'user' && resets.time_zone && memberOption) {
        resets.time_zone = memberOption.for;
        resets.time_zone_mode = 'user';
      }

      this.setFilter({ changes: resets, skipWidgets: this.shouldSkipWidgetReload });
    },

    // Applies a partial change to the shared filters in a single setFilter, refreshing the timezone
    // options for the resulting scope and (unless resetTimeZone is false) resetting the selected
    // timezone in the same write when it is no longer valid for that scope. `memberTimeZone` is the
    // zone of a member picked on a Timeline row: it becomes the selection.
    applyScopeChange(changes, { resetTimeZone = true, skipWidgets = false, memberTimeZone = null } = {}) {
      const applied = memberTimeZone
        ? { ...changes, time_zone: memberTimeZone, time_zone_mode: 'user' }
        : changes;
      const pending = { ...this.filters, ...applied };
      const options = this.timeZoneOptionsForCurrentFilter({
        date: pending.date,
        userId: pending.user_id,
        timeZone: memberTimeZone,
      });

      this.applyTimeZoneOptions(options);
      const resets = resetTimeZone && !memberTimeZone
        ? { ...this.invalidTimeZoneReset(options, pending), ...multiDayZoneReset(pending, this.organization.time_zone?.for) }
        : {};

      this.setFilter({
        changes: { ...applied, ...resets },
        skipWidgets: skipWidgets || this.shouldSkipWidgetReload,
      });
    },

    // The picked row's `mode` is stored too, so two rows on one zone stay apart.
    changeTimeZone(tzParam, mode = null) {
      // `filters.time_zone` is null until someone picks one or the URL carries one, and the header
      // shows the org zone all the while - so fall back to what is displayed, or re-picking it
      // would count as a change.
      const previous = this.filters.time_zone_mode === INSIGHTS_TIME_ZONE_MODE_MEMBERS
        ? INSIGHTS_TIME_ZONE_MODE_MEMBERS
        : this.filters.time_zone || this.activeTimeZone?.for;
      // No stored mode means the row was matched by zone, so the ticked row's mode stands in.
      const currentMode = this.filters.time_zone_mode ?? this.activeTimeZone?.mode ?? null;

      if (previous === tzParam && mode === currentMode) {
        return;
      }

      const changes = mode === INSIGHTS_TIME_ZONE_MODE_MEMBERS
        ? { time_zone_mode: mode }
        : { time_zone: tzParam, time_zone_mode: mode };

      this.setFilter({ changes, skipWidgets: this.ownsTimeZoneRefetch });
      this.trackTimeZoneChange(previous, tzParam, mode);
    },
    // Only fires on a real change - re-picking the row already in effect returns above untracked.
    trackTimeZoneChange(previousValue, newValue, mode) {
      this.trackAnalytics('Insights timezone change', {
        previous_value: previousValue,
        new_value: newValue,
        option: mode,
        tab: this.filters.tab,
      });
    },

    setConfidenceLevelsFilter(confidenceLevels) {
      this.setFilter({ changes: { confidence_levels: confidenceLevels } });
    },

    backButtonLink() {
      let string = 'all_user_id';

      if (this.previousDashboardIsIndividualTeam && this.currentUserBelongsToPrevTeam) {
        string = this.previousView;
      }

      this.setPreviousView({ previousView: null, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string);
    },
  },
};
